function mountProductBannerSlider(root) {
  if (!root) return;

  const section = root.closest('.shopify-section');
  const slidesHost = root.querySelector('.product-banner-slider__slides');
  const thumbnailsMount = root.querySelector('[data-product-banner-thumbnails]');
  const backgroundsMount = section?.querySelector('[data-product-banner-backgrounds]');

  if (!slidesHost || !backgroundsMount) {
    return;
  }

  if (root.dataset.mounted === 'true') {
    return;
  }

  const slides = Array.from(slidesHost.querySelectorAll('[data-product-banner-slide]')).slice(0, 3);

  backgroundsMount.querySelector('.image-banner-slider__media--fallback')?.remove();

  slides.forEach((slide, index) => {
    slide.dataset.slideIndex = String(index);

    const background = slide.querySelector('[data-product-banner-background]');
    if (background) {
      background.dataset.slideIndex = String(index);
      backgroundsMount.appendChild(background);
    }

    const thumbnail = slide.querySelector('[data-product-banner-thumbnail]');
    if (thumbnail && thumbnailsMount) {
      thumbnail.dataset.slideIndex = String(index);
      thumbnail.id = `ProductBannerThumb-${root.id || 'slider'}-${index}`;
      thumbnailsMount.appendChild(thumbnail);
    }

    const template = slide.querySelector('[data-product-banner-card-template]');
    if (template) {
      template.dataset.slideIndex = String(index);
      root.appendChild(template);
    }

    slide.remove();
  });

  slidesHost.remove();
  root.dataset.mounted = 'true';
}

class ProductBannerSlider extends HTMLElement {
  constructor() {
    super();
    this._onThumbnailClick = this._onThumbnailClick.bind(this);
    this._onBlockSelect = this._onBlockSelect.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }

  init() {
    if (this.dataset.initialized === 'true') {
      this.destroy();
    }

    this.section = this.closest('.shopify-section');
    if (!this.section) return;

    mountProductBannerSlider(this);

    const backgroundsMount = this.section.querySelector('[data-product-banner-backgrounds]');
    this.backgrounds = Array.from(
      backgroundsMount?.querySelectorAll('[data-product-banner-background]') || []
    );
    this.thumbnails = Array.from(this.querySelectorAll('[data-product-banner-thumbnail]'));
    this.templates = Array.from(this.querySelectorAll('[data-product-banner-card-template]'));
    this.card = this.querySelector('[data-product-banner-card]');

    this.thumbnails?.forEach((thumbnail) => {
      thumbnail.addEventListener('click', this._onThumbnailClick);
    });
    this.section.addEventListener('shopify:block:select', this._onBlockSelect);

    const initialIndex = this.thumbnails?.findIndex((thumbnail) => thumbnail.classList.contains('is-active'));
    this.activate(initialIndex >= 0 ? initialIndex : 0, false);

    this.dataset.initialized = 'true';
  }

  destroy() {
    this.thumbnails?.forEach((thumbnail) => {
      thumbnail.removeEventListener('click', this._onThumbnailClick);
    });
    this.section?.removeEventListener('shopify:block:select', this._onBlockSelect);
    delete this.dataset.initialized;
    delete this.dataset.mounted;
  }

  _onThumbnailClick(event) {
    const thumbnail = event.currentTarget;
    const index = Number(thumbnail.dataset.slideIndex);
    if (Number.isNaN(index)) return;
    this.activate(index);
  }

  _onBlockSelect(event) {
    if (!this.section?.contains(event.target)) return;

    const blockId = event.detail?.blockId;
    if (!blockId) return;

    const thumbnail = this.thumbnails?.find((item) => item.dataset.blockId === blockId);
    if (thumbnail) {
      this.activate(Number(thumbnail.dataset.slideIndex), false);
      return;
    }

    const background = this.backgrounds?.find((item) => item.dataset.blockId === blockId);
    if (background) {
      this.activate(Number(background.dataset.slideIndex), false);
      return;
    }

    const template = this.templates?.find((item) => item.dataset.blockId === blockId);
    if (template) {
      this.activate(Number(template.dataset.slideIndex), false);
    }
  }

  activate(index, focusThumbnail = true) {
    if (!this.thumbnails?.length || index < 0 || index >= this.thumbnails.length) return;

    this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
      const isActive = thumbnailIndex === index;
      thumbnail.classList.toggle('is-active', isActive);
      thumbnail.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.backgrounds?.forEach((background) => {
      const isActive =
        Number(background.dataset.slideIndex) === index ||
        (this.thumbnails[index]?.dataset.blockId &&
          background.dataset.blockId === this.thumbnails[index].dataset.blockId);
      background.classList.toggle('is-active', isActive);
      background.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    const template = this.templates?.find((item) => Number(item.dataset.slideIndex) === index);
    if (this.card) {
      const cardContent = template?.content.querySelector('[data-product-banner-card-content]');
      const hasCard = template?.dataset.hasCard !== 'false' && Boolean(cardContent);

      if (hasCard) {
        this.card.hidden = false;
        this.card.href = template.dataset.productUrl || '#';
        this.card.innerHTML = cardContent.innerHTML;
        this.applyCardSettings();
      } else {
        this.card.hidden = true;
        this.card.removeAttribute('href');
        this.card.innerHTML = '';
      }
    }

    const activeThumbnail = this.thumbnails[index];
    if (activeThumbnail && this.card && !this.card.hidden) {
      this.card.setAttribute('aria-labelledby', activeThumbnail.id);
    } else if (this.card) {
      this.card.removeAttribute('aria-labelledby');
    }

    if (focusThumbnail && activeThumbnail) {
      activeThumbnail.focus();
    }
  }

  applyCardSettings() {
    if (!this.card) return;

    const titlePreset = this.dataset.titlePreset || 'h6';
    const title = this.card.querySelector('[data-product-banner-title]');
    if (title) {
      ['h5', 'h6', 'subheading', 'caption', 'p'].forEach((preset) => {
        title.classList.toggle(preset, preset === titlePreset);
      });
    }

    const wrapper = this.card.querySelector('.product-card-mini__wrapper');
    if (wrapper) {
      wrapper.classList.toggle('product-card-mini--no-media', this.dataset.showImage !== 'true');
    }
  }
}

if (!customElements.get('product-banner-slider')) {
  customElements.define('product-banner-slider', ProductBannerSlider);
}

function initProductBannerSliders(scope = document) {
  scope.querySelectorAll('product-banner-slider:not([data-initialized="true"])').forEach((element) => {
    if (typeof element.init === 'function') {
      element.init();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initProductBannerSliders());
} else {
  initProductBannerSliders();
}

document.addEventListener('shopify:section:load', (event) => {
  initProductBannerSliders(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('product-banner-slider').forEach((element) => {
    if (typeof element.destroy === 'function') {
      element.destroy();
    }
  });
});
